import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import useWatchlistReviewIdentity from './useWatchlistReviewIdentity';
import { watchlistTableFrameSx } from '../../styles/watchlistMsiStyles';

const text = (value) => (value == null ? '' : String(value).trim());

/**
 * Rows a Watchlist upload or linked-Excel sync did not consider because the
 * row itself had a mistake, from its input-validation errors. A structural
 * error (no identifier column) names no row.
 */
function skippedUploadRows(errors) {
    return (Array.isArray(errors) ? errors : [])
        .filter((error) => error && typeof error === 'object' && Number(error.row_index) >= 0)
        .map((error) => ({
            rowNumber: Number.isInteger(error.row_number) ? error.row_number : null,
            identifierType: text(error.identifier_type).toLowerCase(),
            identifier: text(error.raw_value),
            // Set only for a row that could have held a security, e.g. an
            // unknown country; never for a duplicate, whose first row stays.
            identifierValue: text(error.identifier_value),
            country: text(error.uploaded_country),
            problem: text(error.hint || error.reason || error.code),
        }));
}

/**
 * The rows an upload or sync did not consider (the list may be capped), how
 * many there were in all, and whether they came from the linked workbook.
 */
export function notConsideredRows(inputValidation) {
    const rows = skippedUploadRows(inputValidation?.errors);
    return {
        rows,
        total: Math.max(Number(inputValidation?.total_error_count) || 0, rows.length),
        linkedExcel: inputValidation?.ingress_kind === 'm365_excel',
    };
}

/**
 * Whether an upload or sync that opened no review skipped a row the user must
 * fix. A duplicate needs none: its first row was used. The code counts cover
 * every row; the error list is capped.
 */
export function hasRowsToFix(inputValidation) {
    const counts = inputValidation?.error_code_counts;
    const codes = counts && typeof counts === 'object'
        ? Object.keys(counts).filter((code) => Number(counts[code]) > 0)
        : (Array.isArray(inputValidation?.errors) ? inputValidation.errors : [])
            .filter((error) => Number(error?.row_index) >= 0)
            .map((error) => error?.code);
    return codes.some((code) => code !== 'duplicate_identifier_row');
}

const RESTORABLE_IDENTIFIER_LABELS = Object.freeze({ cusip: 'CUSIP', sedol: 'SEDOL' });

/**
 * One line naming the identifiers whose leading zeros, dropped when Excel
 * stored them as numbers, the server put back after their check digit
 * verified. Empty when it restored none.
 */
export function restoredLeadingZerosNote(inputValidation) {
    const count = Number(inputValidation?.leading_zero_restored_count) || 0;
    if (count <= 0) return '';
    const label = RESTORABLE_IDENTIFIER_LABELS[
        text(inputValidation?.identifier_column).toLowerCase()
    ] || 'identifier';
    return `Restored the leading zeros Excel dropped from ${count} ${label}${count === 1 ? '' : 's'}.`;
}

/** The skipped row a removed Watchlist security came from, if any. */
export function skippedRowForSecurity(rows, security) {
    const identifierType = text(security?.inbound_id_type).toLowerCase();
    const identifierValue = text(security?.inbound_id_value);
    if (!identifierType || !identifierValue) return null;
    return rows.find((row) => (
        row.identifierType === identifierType && row.identifierValue === identifierValue
    )) || null;
}

/** What to do about rows that were not considered, and whether all are shown. */
export function notConsideredText({ linkedExcel = false, shown, total }) {
    const fix = linkedExcel
        ? 'Fix them in the linked workbook and sync again to include them.'
        : 'Fix them in the file and upload it again to include them.';
    const partial = total > shown ? ` Showing the first ${shown} of ${total}.` : '';
    return `Rows with a mistake in the row itself. ${fix}${partial}`;
}

export default function SkippedUploadRowsTable({ rows }) {
    const identity = useWatchlistReviewIdentity();
    return (
        <>
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={[{ borderRadius: 1.5, maxHeight: 320 }, watchlistTableFrameSx]}
        >
            <Table size="small" stickyHeader aria-label="Rows not considered">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 64, fontWeight: 700 }}>Row</TableCell>
                        <TableCell sx={{ width: '28%', minWidth: 220, fontWeight: 700 }}>{identity.header}</TableCell>
                        <TableCell sx={{ width: '18%', fontWeight: 700 }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Problem</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.rowNumber ?? 'row'}-${index}`}>
                            <TableCell>{row.rowNumber ?? '—'}</TableCell>
                            <TableCell sx={{ overflowWrap: 'anywhere' }}>
                                {identity.renderIdentity({
                                    ...row,
                                    inbound_id_type: row.identifierType,
                                    inbound_id_value: row.identifier || '(blank)',
                                })}
                            </TableCell>
                            <TableCell>{row.country || '—'}</TableCell>
                            <TableCell>{row.problem || '—'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        {identity.selector}
        </>
    );
}

SkippedUploadRowsTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({
        rowNumber: PropTypes.number,
        identifierType: PropTypes.string,
        identifier: PropTypes.string,
        country: PropTypes.string,
        problem: PropTypes.string,
    })).isRequired,
};

/**
 * Names the rows of an upload or sync that opened no review: every row failed,
 * or the rest already matches the Watchlist.
 */
export function SkippedRowsDialog({
    open,
    title,
    lead = '',
    inputValidation = null,
    onClose,
}) {
    const { rows, total, linkedExcel } = notConsideredRows(inputValidation);
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="watchlist-skipped-rows-title"
        >
            <DialogTitle id="watchlist-skipped-rows-title">{title}</DialogTitle>
            <DialogContent dividers>
                {lead && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {lead}
                    </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {inputValidation?.summary_message
                        || 'No rows could be used. Fix them and try again.'}
                </Typography>
                {inputValidation?.identifier_column && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Identifier column:{' '}
                        <strong>{String(inputValidation.identifier_column).toUpperCase()}</strong>
                        {typeof inputValidation.row_count_total === 'number'
                            && ` · Rows: ${inputValidation.row_count_total}`}
                        {typeof inputValidation.error_count === 'number'
                            && ` · Not considered: ${inputValidation.error_count}`}
                    </Typography>
                )}
                {rows.length > 0 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.75 }}>
                            {notConsideredText({ linkedExcel, shown: rows.length, total })}
                        </Typography>
                        <SkippedUploadRowsTable rows={rows} />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

SkippedRowsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    lead: PropTypes.string,
    inputValidation: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};
